const parseTimeAmount = (timeInput: string): number | null => {
  if (!timeInput.trim()) return null;

  const input = timeInput.trim().toLowerCase();

  // 1. Полный формат с неделями, днями, часами и минутами (например: 1н 2д 3ч 30м, 1w 2d 3h 30m)
  let match = input.match(
    /^(?:(\d+)\s*[wн])?\s*(?:(\d+)\s*[dд])?\s*(?:(\d+)\s*[hч])?\s*(?:(\d+)\s*[mм])?$/,
  );

  if (match) {
    const weeks = match[1] ? parseInt(match[1], 10) : 0;
    const days = match[2] ? parseInt(match[2], 10) : 0;
    const hours = match[3] ? parseInt(match[3], 10) : 0;
    const minutes = match[4] ? parseInt(match[4], 10) : 0;

    const minutesLimit = weeks + days + hours > 0 ? 59 : 525600;

    if (weeks > 52 || days > 365 || hours > 8760 || minutes > minutesLimit) return null;

    // 1 неделя = 5 рабочих дней, 1 день = 8 часов
    const total = weeks * 5 * 8 * 60 + days * 8 * 60 + hours * 60 + minutes;

    return total;
  }

  // 2. Формат часы:минуты (например, 1:30, 25:30)
  match = input.match(/^(\d+):(\d{1,2})$/);

  if (match) {
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);

    if (hours > 8760 || minutes > 59) return null;

    const total = hours * 60 + minutes;

    return total;
  }

  // 3. Формат "1 30" — часы и минуты через пробел
  match = input.match(/^(\d+)\s+(\d{1,2})$/);

  if (match) {
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);

    if (hours > 8760 || minutes > 59) return null;

    const total = hours * 60 + minutes;

    return total;
  }

  // 4. Только часы (например, 2ч или 2h, 25ч)
  match = input.match(/^(\d+)\s*[hч]$/);

  if (match) {
    const hours = parseInt(match[1], 10);

    if (hours > 8760) return null;

    const total = hours * 60;

    return total;
  }

  // 5. Только недели (например, 2н или 2w)
  match = input.match(/^(\d+)\s*[wн]$/);

  if (match) {
    const weeks = parseInt(match[1], 10);

    if (weeks > 52) return null;

    // 1 неделя = 5 рабочих дней = 40 часов = 2400 минут
    const total = weeks * 5 * 8 * 60;

    return total;
  }

  // 6. Только дни (например, 3д или 3d)
  match = input.match(/^(\d+)\s*[dд]$/);

  if (match) {
    const days = parseInt(match[1], 10);

    if (days > 365) return null;

    // 1 день = 8 часов = 480 минут
    const total = days * 8 * 60;

    return total;
  }

  // 7. Только минуты (например, 45м или 45m)
  match = input.match(/^(\d+)\s*[mм]$/);

  if (match) {
    const minutes = parseInt(match[1], 10);

    if (minutes > 525600) return null; // максимум год в минутах

    return minutes;
  }

  // 8. Просто число (интерпретируем как минуты)
  match = input.match(/^(\d+)$/);

  if (match) {
    const minutes = parseInt(match[1], 10);

    if (minutes <= 525600) return minutes;
  }

  return null;
};

export const parseTimeInput = (timeInput: string): number | null => {
  const minutes = parseTimeAmount(timeInput);

  return minutes !== null && minutes > 0 ? minutes : null;
};

const MULTIPLIER_REGEX = /^\d+(?:[.,]\d+)?$/;
const MULTIPLY_OPERATOR_REGEX = /[*×xх]/i;

const parseMultiplier = (factor: string): number | null => {
  if (!MULTIPLIER_REGEX.test(factor)) return null;

  const multiplier = parseFloat(factor.replace(',', '.'));

  return Number.isFinite(multiplier) ? multiplier : null;
};

/**
 * Парсит произведение времени на числа: "1ч 30м * 3", "2 x 45м", "30 * 1,5"
 * Число без единиц — множитель; если единиц нет ни у одного сомножителя, минутами считается первое целое
 */
const parseTimeProduct = (term: string): number | null => {
  const factors = term.split(MULTIPLY_OPERATOR_REGEX).map((factor) => factor.trim());
  const timeFactorIndexes = factors
    .map((factor, index) => (MULTIPLIER_REGEX.test(factor) ? -1 : index))
    .filter((index) => index !== -1);

  if (timeFactorIndexes.length > 1) return null;

  const timeIndex = timeFactorIndexes[0] ?? factors.findIndex((factor) => /^\d+$/.test(factor));

  if (timeIndex === -1) return null;

  const minutes = parseTimeAmount(factors[timeIndex]);
  const multipliers = factors.filter((_, index) => index !== timeIndex).map(parseMultiplier);

  if (minutes === null || multipliers.includes(null)) return null;

  return multipliers.reduce<number>((product, multiplier) => product * (multiplier ?? 1), minutes);
};

/**
 * Считает выражение со сложением, вычитанием и умножением времени; умножение выполняется первым
 * Возвращает итог в минутах, в том числе нулевой или отрицательный; null — если выражение не разбирается
 */
export const evaluateTimeExpression = (timeInput: string): number | null => {
  const chunks = timeInput
    .trim()
    .replace(/[−–—]/g, '-')
    .split(/(?=[+-])/);
  let totalMinutes = 0;
  let sign = 1;
  let hasTerms = false;

  // Подряд идущие знаки перемножаются: "1ч - -30м" -> 90, "1ч - +30м" -> 30
  for (const chunk of chunks) {
    const term = chunk.replace(/^[+-]/, '').trim();

    sign *= chunk.startsWith('-') ? -1 : 1;

    if (!term) continue;

    const minutes = parseTimeProduct(term);

    if (minutes === null) return null;

    totalMinutes += sign * minutes;
    sign = 1;
    hasTerms = true;
  }

  if (!hasTerms) return null;

  // toFixed убирает погрешность float: 4,7 * 0,7 * 150 = 493,4999… должно округляться как 493,5
  return Math.round(Number(totalMinutes.toFixed(6)));
};

/**
 * Парсит выражение в положительное количество минут
 * Например: "1ч 20м + 40м + 2ч 10м" -> 250, "8ч - 1ч 30м" -> 390, "45м * 3 - 15м" -> 120
 */
export const parseTimeExpression = (timeInput: string): number | null => {
  const minutes = evaluateTimeExpression(timeInput);

  return minutes !== null && minutes > 0 ? minutes : null;
};
